import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { RouterModule } from '@angular/router';
import { normalizeUserRole, UserRole } from '../../core/enums/user-role.enum';
import { AuthService } from '../../core/services/auth.service';
import { FEATURE_KEYS } from '../../core/models/feature-flag.model';
import { FeatureFlagService } from '../../core/services/feature-flag.service';

interface OperationsCard {
  icon: string;
  label: string;
  description: string;
  route: string;
  metric: string;
}

interface OverviewContent {
  eyebrow: string;
  title: string;
  actionLabel: string;
  actionRoute: string;
  priorityEyebrow: string;
  priorityTitle: string;
  priorities: string[];
  cards: OperationsCard[];
  workflow: Array<{ label: string; status: string; icon: string }>;
}

@Component({
  selector: 'app-platform-operations-overview',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './platform-operations-overview.component.html',
  styleUrls: ['./platform-operations-overview.component.css']
})
export class PlatformOperationsOverviewComponent {
  operatorBookingEnabled = false;

  constructor(private authService: AuthService, private featureFlags: FeatureFlagService) {
    this.featureFlags.effectiveFeatures$.subscribe(states => {
      this.operatorBookingEnabled = states[FEATURE_KEYS.operatorBookingManagement] === true;
    });
    this.featureFlags.loadEffectiveFeatures().subscribe({ error: () => undefined });
  }

  get content(): OverviewContent {
    const role = this.getCurrentRole();

    if (role === UserRole.ADMIN) {
      return this.adminContent;
    }

    if (role === UserRole.OPERATOR) {
      if (this.operatorBookingEnabled) return this.operatorContent;
      return {
        ...this.operatorContent,
        priorityTitle: 'Keep vehicles, routes, and trips moving',
        priorities: this.operatorContent.priorities.filter(item => !item.toLowerCase().includes('booking')),
        workflow: this.operatorContent.workflow.filter(item => !item.status.toLowerCase().includes('booking')),
        cards: this.operatorContent.cards.filter(card => card.route !== '/dashboard/bookings')
      };
    }

    if (role === UserRole.PASSENGER) {
      return this.passengerContent;
    }

    return this.platformEmployeeContent;
  }

  private getCurrentRole(): UserRole {
    const currentUser = this.authService.getCurrentUser();
    return currentUser
      ? normalizeUserRole(currentUser.userRoleName || currentUser.userRole || currentUser.userRoleID)
      : UserRole.PLATFORM_EMPLOYEE;
  }

  private adminContent: OverviewContent = {
    eyebrow: 'Admin workspace',
    title: 'System administration',
    actionLabel: 'Manage users',
    actionRoute: '/dashboard/users',
    priorityEyebrow: 'Administration priority',
    priorityTitle: 'Control platform access, operators, and reports',
    priorities: [
      'Manage users and role assignments',
      'Review operator organisations and activation status',
      'Monitor all fleet activity and operational health',
      'Maintain devices, SIMs, vehicles, routes, trips, and bookings'
    ],
    workflow: [
      { icon: 'admin_panel_settings', label: 'Access control', status: 'Create users, assign roles, and manage account status' },
      { icon: 'business', label: 'Operator governance', status: 'Review organisations, Powerlynx locations, and activation readiness' },
      { icon: 'devices', label: 'Fleet infrastructure', status: 'Maintain device and SIM inventory across operators' },
      { icon: 'monitor_heart', label: 'Operational oversight', status: 'Use heartbeat, notifications, maps, and bookings to resolve issues' }
    ],
    cards: [
      {
        icon: 'people',
        label: 'Users',
        description: 'Create, update, and govern platform employees, admins, operators, and passengers.',
        route: '/dashboard/users',
        metric: 'Access'
      },
      {
        icon: 'business',
        label: 'Operators',
        description: 'Manage operator organisations, activation status, and hotspot mapping codes.',
        route: '/dashboard/organisations',
        metric: 'Organisations'
      },
      {
        icon: 'map',
        label: 'Fleet Monitor',
        description: 'Monitor live vehicle movement, stale GPS data, and route progress across the platform.',
        route: '/dashboard/live-map',
        metric: 'All fleets'
      },
      {
        icon: 'monitor_heart',
        label: 'Reports',
        description: 'Review operational notifications and delivery failures.',
        route: '/dashboard/reports',
        metric: 'Notifications'
      }
    ]
  };

  private platformEmployeeContent: OverviewContent = {
    eyebrow: 'Riya Shuma operations',
    title: 'Platform command centre',
    actionLabel: 'Open fleet monitor',
    actionRoute: '/dashboard/live-map',
    priorityEyebrow: 'Operations priority',
    priorityTitle: 'Make onboarding and monitoring effortless',
    priorities: [
      'Register operator organisations',
      'Provision devices and SIMs',
      'Monitor all live fleet activity',
      'Investigate notifications from reports'
    ],
    workflow: [
      { icon: 'business', label: 'Operator intake', status: 'Capture organisation, contacts, and Powerlynx location readiness' },
      { icon: 'badge', label: 'User setup', status: 'Invite operator users and assign platform permissions' },
      { icon: 'router', label: 'Device provisioning', status: 'Prepare tracker devices, SIMs, and RouterOS scripts' },
      { icon: 'monitor_heart', label: 'Operational monitoring', status: 'Watch fleet heartbeat, stale GPS, and notifications' }
    ],
    cards: [
      {
        icon: 'map',
        label: 'Fleet Monitor',
        description: 'Live view of vehicles, trips, stale GPS data, and route progress across operators.',
        route: '/dashboard/live-map',
        metric: 'All fleets'
      },
      {
        icon: 'business',
        label: 'Operators',
        description: 'Create and manage operator organisations before vehicles, users, and devices are assigned.',
        route: '/dashboard/organisations',
        metric: 'Organisations'
      },
      {
        icon: 'devices',
        label: 'Device Operations',
        description: 'Track devices, SIMs, activation status, and onboarding script generation.',
        route: '/dashboard/devices',
        metric: 'Devices + SIMs'
      },
      {
        icon: 'monitor_heart',
        label: 'Reports',
        description: 'Review operational notifications and delivery failures.',
        route: '/dashboard/reports',
        metric: 'Notifications'
      }
    ]
  };

  private operatorContent: OverviewContent = {
    eyebrow: 'Operator workspace',
    title: 'Fleet control room',
    actionLabel: 'Open live map',
    actionRoute: '/dashboard/live-map',
    priorityEyebrow: 'Today focus',
    priorityTitle: 'Keep vehicles, routes, and bookings moving',
    priorities: [
      'Monitor active vehicles',
      'Schedule and update trips',
      'Review passenger bookings',
      'Maintain routes and stop information'
    ],
    workflow: [
      { icon: 'directions_bus', label: 'Fleet readiness', status: 'Confirm vehicles and device assignments before service' },
      { icon: 'route', label: 'Route coverage', status: 'Keep stops and recurring route patterns up to date' },
      { icon: 'schedule', label: 'Trip dispatch', status: 'Assign drivers, seats, prices, and departure times' },
      { icon: 'groups', label: 'Passenger operations', status: 'Review manifests, bookings, and payment state' }
    ],
    cards: [
      {
        icon: 'directions_bus',
        label: 'My Fleet',
        description: 'Manage vehicle records, assignments, device links, and fleet readiness.',
        route: '/dashboard/vehicles',
        metric: 'Vehicles'
      },
      {
        icon: 'route',
        label: 'Routes',
        description: 'Maintain route details, stops, recurrence patterns, and operational coverage.',
        route: '/dashboard/routes',
        metric: 'Route network'
      },
      {
        icon: 'schedule',
        label: 'Trip Scheduling',
        description: 'Plan departures, assign vehicles and drivers, and manage seat availability.',
        route: '/dashboard/trips',
        metric: 'Trips'
      },
      {
        icon: 'book_online',
        label: 'Bookings',
        description: 'Track passenger bookings, payment status, manifests, and cancellation activity.',
        route: '/dashboard/bookings',
        metric: 'Passengers'
      }
    ]
  };

  private passengerContent: OverviewContent = {
    eyebrow: 'Passenger portal',
    title: 'Plan and track your trip',
    actionLabel: 'Find trips',
    actionRoute: '/dashboard/search',
    priorityEyebrow: 'At a glance',
    priorityTitle: 'Know where to go, when to board, and what is happening',
    priorities: [
      'Search available trips',
      'Track vehicle location and ETA',
      'Manage current bookings',
      'Subscribe to trip updates'
    ],
    workflow: [
      { icon: 'search', label: 'Search', status: 'Find trips by operator, route, stop, date, and time' },
      { icon: 'event_seat', label: 'Book', status: 'Reserve seats and receive booking confirmation' },
      { icon: 'near_me', label: 'Track', status: 'See live trip location, ETA, and boarding context' },
      { icon: 'notifications_active', label: 'Updates', status: 'Subscribe to trip alerts and shared trip links' }
    ],
    cards: [
      {
        icon: 'search',
        label: 'Find Trips',
        description: 'Search public operators, routes, stops, dates, and available trips.',
        route: '/dashboard/search',
        metric: 'Search'
      },
      {
        icon: 'map',
        label: 'Trip Map',
        description: 'View route context, selected vehicle movement, and passenger-friendly status.',
        route: '/dashboard/live-map',
        metric: 'Live info'
      },
      {
        icon: 'confirmation_number',
        label: 'My Bookings',
        description: 'Review booking status, ticket details, and upcoming journey information.',
        route: '/dashboard/bookings',
        metric: 'Bookings'
      },
      {
        icon: 'person',
        label: 'Profile',
        description: 'Keep passenger details ready for faster booking and trip communication.',
        route: '/dashboard/profile',
        metric: 'Account'
      }
    ]
  };
}
